import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Button, Input, Select } from '../ui'
import { createBrainWorkOrder, createBrainWorkOrderFromAudio } from '../../api/brain'

interface WorkOrderComposeProps {
  onClose: () => void
  onCreated: () => void
  brainOptions?: Array<{ value: string; label: string }>
  initialSubject?: string
  initialInstructions?: string
  initialTargetBrain?: string
  mobileTakeover?: boolean
}

const DEFAULT_BRAINS = [
  { value: 'decisive', label: 'Decisive' },
  { value: 'mech-browse.gm', label: 'ThinkBrowse' },
]

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

export function WorkOrderCompose({
  onClose,
  onCreated,
  brainOptions,
  initialSubject = '',
  initialInstructions = '',
  initialTargetBrain = 'decisive',
  mobileTakeover = false,
}: WorkOrderComposeProps) {
  const [mode, setMode] = useState<'manual' | 'audio'>('manual')
  const [subject, setSubject] = useState(initialSubject)
  const [instructions, setInstructions] = useState(initialInstructions)
  const [priority, setPriority] = useState('medium')
  const [targetBrain, setTargetBrain] = useState(initialTargetBrain)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [recording, setRecording] = useState(false)
  const [recordingSupported, setRecordingSupported] = useState(false)
  const [audioResultMessage, setAudioResultMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const resolvedBrainOptions = brainOptions && brainOptions.length > 0 ? brainOptions : DEFAULT_BRAINS

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [handleEscape])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // priority and mode intentionally reset to defaults ('medium'/'manual') on re-mount — no prop support by design
  useEffect(() => {
    setSubject(initialSubject)
    setInstructions(initialInstructions)
    setTargetBrain(initialTargetBrain)
    setPriority('medium')
    setMode('manual')
    setAudioFile(null)
    setAudioResultMessage(null)
    setError(null)
    setSuccess(false)
  }, [initialInstructions, initialSubject, initialTargetBrain])

  useEffect(() => {
    setRecordingSupported(
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined',
    )
  }, [])

  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => {
      onCreated()
      onClose()
    }, 800)
    return () => clearTimeout(timer)
  }, [success, onCreated, onClose])

  useEffect(() => {
    return () => {
      mediaRecorder?.stop()
      mediaStream?.getTracks().forEach((track) => track.stop())
    }
  }, [mediaRecorder, mediaStream])

  useEffect(() => {
    const hasSelectedTarget = resolvedBrainOptions.some((option) => option.value === targetBrain)
    if (!hasSelectedTarget && resolvedBrainOptions.length > 0) {
      setTargetBrain(resolvedBrainOptions[0].value)
    }
  }, [resolvedBrainOptions, targetBrain])

  const handleSubmit = async () => {
    setError(null)
    setAudioResultMessage(null)
    setSubmitting(true)

    try {
      await createBrainWorkOrder({
        from: 'founder',
        to: targetBrain,
        subject,
        body: { instructions },
        priority: priority as 'low' | 'medium' | 'high' | 'critical',
      })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create work order')
    } finally {
      setSubmitting(false)
    }
  }

  const startRecording = async () => {
    setError(null)
    setAudioResultMessage(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      const chunks: BlobPart[] = []

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) chunks.push(event.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' })
        const recorded = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type })
        setAudioFile(recorded)
      }

      recorder.start()
      setMediaStream(stream)
      setMediaRecorder(recorder)
      setRecording(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone access failed')
    }
  }

  const stopRecording = () => {
    mediaRecorder?.stop()
    mediaStream?.getTracks().forEach((track) => track.stop())
    setMediaRecorder(null)
    setMediaStream(null)
    setRecording(false)
  }

  const handleAudioSubmit = async () => {
    if (!audioFile) return
    setError(null)
    setAudioResultMessage(null)
    setSubmitting(true)

    try {
      const result = await createBrainWorkOrderFromAudio({
        audio: audioFile,
        to: targetBrain,
        priority: priority as 'low' | 'medium' | 'high' | 'critical',
      })
      if (result.created) {
        setAudioResultMessage(
          result.degraded
            ? 'Audio uploaded. Work order created with manual-processing fallback.'
            : 'Audio processed and work order created.',
        )
        setSuccess(true)
      } else {
        setAudioResultMessage('Audio processed as PERSONAL. No work order created.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process audio')
    } finally {
      setSubmitting(false)
    }
  }

  const sheet = (
    <div
      className="flex h-full w-full flex-col overflow-hidden bg-swiss-white sm:h-auto sm:max-h-[90vh] sm:max-w-[480px] sm:border sm:border-swiss-gray-200"
      onClick={(e) => e.stopPropagation()}
    >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-swiss-gray-200 bg-swiss-white px-4 py-4 sm:px-6">
          <h2 className="text-swiss-h2 font-swiss-sans font-semibold">
            New Work Order
          </h2>
          <button
            onClick={onClose}
            className="text-swiss-gray-500 hover:text-swiss-black transition-colors duration-150 cursor-pointer p-1"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="square"
              />
            </svg>
          </button>
        </div>

        {error && (
          <div className="px-4 pt-4 text-swiss-red text-swiss-small sm:px-6">
            {error}
          </div>
        )}

        {success && (
          <div className="px-4 pt-4 text-swiss-green text-swiss-small sm:px-6">
            Work order created successfully.
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-4 pb-6">
          <div className="flex border border-swiss-gray-200">
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`flex-1 px-3 py-2 text-swiss-small cursor-pointer ${mode === 'manual' ? 'bg-swiss-black text-swiss-white' : 'bg-swiss-white text-swiss-black'}`}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setMode('audio')}
              className={`flex-1 px-3 py-2 text-swiss-small cursor-pointer ${mode === 'audio' ? 'bg-swiss-black text-swiss-white' : 'bg-swiss-white text-swiss-black'}`}
            >
              Audio
            </button>
          </div>

          {mode === 'manual' ? (
            <>
              <Input
                label="Subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of work"
              />

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="wo-instructions"
                  className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500"
                >
                  Instructions
                </label>
                <textarea
                  id="wo-instructions"
                  className="w-full min-h-[120px] resize-none border border-swiss-gray-200 p-3 text-swiss-body font-swiss-sans focus:outline-none focus:border-swiss-black transition-colors duration-150"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Detailed instructions for the agent..."
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500">
                  Audio File
                </label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    setAudioFile(file)
                    setAudioResultMessage(null)
                  }}
                  className="border border-swiss-gray-200 p-2 text-swiss-small"
                />
                {recordingSupported && (
                  <div className="flex gap-2">
                    {!recording ? (
                      <Button variant="secondary" onClick={startRecording}>
                        Start Recording
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={stopRecording}>
                        Stop Recording
                      </Button>
                    )}
                  </div>
                )}
                {audioFile && (
                  <div className="text-swiss-small text-swiss-gray-500">
                    Selected: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
                {audioResultMessage && (
                  <div className="text-swiss-small text-swiss-gray-500">{audioResultMessage}</div>
                )}
              </div>
            </>
          )}

          <Select
            label="Target Brain"
            options={resolvedBrainOptions}
            value={targetBrain}
            onChange={(e) => setTargetBrain(e.target.value)}
          />

          <Select
            label="Priority"
            options={PRIORITIES}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-col-reverse gap-3 border-t border-swiss-gray-200 bg-swiss-white px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {mode === 'manual' ? (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || !subject.trim() || !instructions.trim()}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleAudioSubmit}
              disabled={submitting || !audioFile}
            >
              {submitting ? 'Processing...' : 'Create From Audio'}
            </Button>
          )}
        </div>
    </div>
  )

  if (mobileTakeover) {
    return (
      <div className="-mx-4 -my-6 min-h-[calc(100dvh-8rem)] bg-swiss-white sm:m-0 sm:min-h-0">
        {sheet}
      </div>
    )
  }

  const modal = (
    <div
      className="fixed inset-0 z-[2147483647] flex items-stretch justify-center bg-swiss-white sm:items-end sm:bg-swiss-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Create work order"
    >
      {sheet}
    </div>
  )

  if (typeof document === 'undefined') {
    return modal
  }

  return createPortal(modal, document.body)
}
